import React from 'react';
import { FaChevronLeft, FaChevronRight, FaLink, FaPlus, FaSyncAlt } from 'react-icons/fa';
import CalendarMonthGrid from './components/CalendarMonthGrid';
import EventListPanel from './components/EventListPanel';
import EventModal from './components/EventModal';
import ShareLinkModal from './components/ShareLinkModal';
import TeamCalendarManageViewModel from './TeamCalendarManage.viewmodel';
import { formatMonthTitle } from './components/dateUtils';

function TeamCalendarManageView() {
    const {
        currentMonth,
        selectedDate,
        selectedDateLabel,
        events,
        shareLinks,
        filters,
        draft,
        shareDraft,
        shareExpiryDays,
        isEventModalOpen,
        isShareModalOpen,
        isLoading,
        isSaving,
        isShareSaving,
        employees,
        isEmployeesLoading,
        selectedDateEvents,
        upcomingEvents,
        availableTeams,
        availableOwners,
        canEditDraft,
        setCurrentMonth,
        setSelectedDate,
        setIsEventModalOpen,
        setIsShareModalOpen,
        openCreateModal,
        openEditModal,
        saveEvent,
        removeEvent,
        createShareLink,
        deleteShareLinkItem,
        copyShareLink,
        reloadEvents,
        updateFilter,
        updateDraft,
        updateShareDraft,
        updateShareExpiryDays
    } = TeamCalendarManageViewModel();

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="mx-auto flex max-w-[1600px] flex-col gap-6 px-4 py-6 lg:px-6">
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">
                                Team Calendar
                            </div>
                            <h1 className="mt-2 text-3xl font-bold text-gray-900">
                                Plan work across teams and people
                            </h1>
                            <p className="mt-2 text-sm text-gray-500">
                                Standalone calendar page with manage controls and public share links.
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                            >
                                <FaChevronLeft />
                            </button>
                            <div className="min-w-[12rem] text-center text-lg font-semibold text-gray-800">
                                {formatMonthTitle(currentMonth)}
                            </div>
                            <button
                                type="button"
                                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                            >
                                <FaChevronRight />
                            </button>
                            <button
                                type="button"
                                onClick={() => reloadEvents()}
                                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                            >
                                <FaSyncAlt />
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const today = new Date();
                                    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                                    setSelectedDate(today);
                                }}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsShareModalOpen(true)}
                                className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                            >
                                <FaLink />
                                Share
                            </button>
                            <button
                                type="button"
                                onClick={() => openCreateModal(selectedDate)}
                                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                            >
                                <FaPlus />
                                Add Event
                            </button>
                        </div>
                    </div>
                    <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-6">
                        <input
                            value={filters.search ?? ''}
                            onChange={event => updateFilter('search', event.target.value)}
                            placeholder="Search title or description"
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                        <select
                            value={filters.teamName ?? ''}
                            onChange={event => updateFilter('teamName', event.target.value)}
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                        >
                            <option value="">All teams</option>
                            {availableTeams.map(teamName => (
                                <option
                                    key={teamName}
                                    value={teamName}
                                >
                                    {teamName}
                                </option>
                            ))}
                        </select>
                        <select
                            value={filters.status ?? ''}
                            onChange={event => updateFilter('status', event.target.value)}
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                        >
                            <option value="">All status</option>
                            <option value="planned">Planned</option>
                            <option value="in-progress">In progress</option>
                            <option value="done">Done</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <select
                            value={filters.ownerCitizenId ?? ''}
                            onChange={event => updateFilter('ownerCitizenId', event.target.value)}
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                        >
                            <option value="">All owners</option>
                            {availableOwners.map(owner => (
                                <option
                                    key={owner.value}
                                    value={owner.value}
                                >
                                    {owner.label}
                                </option>
                            ))}
                        </select>
                        <select
                            value={filters.visibility ?? ''}
                            onChange={event => updateFilter('visibility', event.target.value)}
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                        >
                            <option value="">All visibility</option>
                            <option value="only_me">Only me</option>
                            <option value="selected_people">Selected people</option>
                            <option value="selected_teams">Selected teams</option>
                            <option value="all_menu_users">Everyone in menu</option>
                        </select>
                        <button
                            type="button"
                            onClick={() => {
                                updateFilter('search', '');
                                updateFilter('teamName', '');
                                updateFilter('status', '');
                                updateFilter('ownerCitizenId', '');
                                updateFilter('visibility', '');
                            }}
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                        >
                            Reset filters
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
                    <div>
                        {isLoading && (
                            <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                                Loading calendar events...
                            </div>
                        )}
                        <CalendarMonthGrid
                            currentMonth={currentMonth}
                            events={events}
                            selectedDate={selectedDate}
                            onSelectDate={setSelectedDate}
                            onSelectEvent={openEditModal}
                        />
                    </div>
                    <div className="space-y-6">
                        <EventListPanel
                            title={`Selected Day - ${selectedDateLabel}`}
                            events={selectedDateEvents}
                            onSelectEvent={openEditModal}
                            emptyMessage="No events on this day."
                        />
                        <EventListPanel
                            title="Upcoming"
                            events={upcomingEvents}
                            onSelectEvent={openEditModal}
                            emptyMessage="No upcoming events in this range."
                        />
                        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                                Share Links
                            </div>
                            <div className="mt-3 text-2xl font-bold text-gray-900">{shareLinks.length}</div>
                            <div className="mt-1 text-sm text-gray-500">
                                Public viewer links currently available
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <EventModal
                isOpen={isEventModalOpen}
                draft={draft}
                employees={employees}
                availableTeams={availableTeams}
                isEmployeesLoading={isEmployeesLoading}
                isSaving={isSaving}
                isReadOnly={!canEditDraft}
                onClose={() => setIsEventModalOpen(false)}
                onChange={updateDraft}
                onSubmit={saveEvent}
                onDelete={canEditDraft ? removeEvent : undefined}
            />
            <ShareLinkModal
                isOpen={isShareModalOpen}
                draft={shareDraft}
                shareLinks={shareLinks}
                employees={employees}
                availableTeams={availableTeams}
                expiryDays={shareExpiryDays}
                isSaving={isShareSaving}
                onClose={() => setIsShareModalOpen(false)}
                onChange={updateShareDraft}
                onExpiryDaysChange={updateShareExpiryDays}
                onSubmit={createShareLink}
                onDelete={deleteShareLinkItem}
                onCopy={copyShareLink}
            />
        </div>
    );
}

export default TeamCalendarManageView;
